import React, { useState, memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar, Button, Tooltip, Typography, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { format, parseISO, isSameDay, isPast } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import CrmCardModal from '../../components/CrmCardModal';

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1),
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0px 10px 17px -10px rgba(0, 0, 0, 0.59)',
    marginBottom: theme.spacing(1),
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginRight: theme.spacing(0.5),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    justifyContent: 'space-between',
  },
  leftHeader: { display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 },
  avatar: { marginRight: theme.spacing(0.8), width: theme.spacing(3.5), height: theme.spacing(3.5), flexShrink: 0 },
  cardTitle: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: theme.mode === "light" ? theme.palette.text.primary : "#000",
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardTitleExpired: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: theme.palette.error.main,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ticketNumber: { fontSize: '0.68rem', fontWeight: 'bold', color: "#9e9e9e", flexShrink: 0, marginLeft: 6 },
  divider: { background: "#e6e6e6", margin: '5px 0' },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 3,
    gap: 4,
  },
  infoLabel: {
    fontSize: '0.65rem',
    color: '#9e9e9e',
    flexShrink: 0,
    width: 72,
  },
  infoValue: {
    fontSize: '0.75rem',
    color: '#444',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  infoValueExpired: {
    fontSize: '0.75rem',
    color: theme.palette.error.main,
    fontWeight: 700,
    flex: 1,
  },
  infoValueGreen: {
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    fontWeight: 700,
    flex: 1,
  },
  lastMessageTime: { fontSize: '0.8rem', color: "#757575" },
  lastMessageTimeUnread: {
    fontSize: '0.8rem',
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  crmStatus: {
    fontSize: '0.6rem',
    padding: '1px 5px',
    borderRadius: 10,
    fontWeight: 600,
    display: 'inline-block',
    marginLeft: 4,
    verticalAlign: 'middle',
    flexShrink: 0,
  },
  footer: { display: 'flex', alignItems: 'center', marginTop: theme.spacing(0.8) },
  cardButton: {
    fontSize: '0.5rem',
    padding: '2px 6px',
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '10px',
    '&:hover': { backgroundColor: theme.palette.primary.dark },
  },
  textField: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
  dialogPaper: { borderRadius: '10px' },
  dialogButton: { borderRadius: '10px' },
  expiredBadge: {
    fontSize: '0.6rem',
    color: theme.palette.error.main,
    fontWeight: 700,
    marginBottom: 3,
  },
}));

const CRM_STATUS_COLORS = {
  "Não iniciado": { background: '#eceff1', color: '#546e7a' },
  "Contato": { background: '#e3f2fd', color: '#1565c0' },
  "Em andamento": { background: '#fff8e1', color: '#f9a825' },
  "Proposta": { background: '#ede7f6', color: '#5e35b1' },
  "Follow up": { background: '#e0f2f1', color: '#00796b' },
  "Sucesso": { background: '#e8f5e9', color: '#2e7d32' },
  "Perdido": { background: '#ffebee', color: '#c62828' },
};

const KanbanCard = memo(({ ticket, index, updateTicket, laneOptions }) => {
  const classes = useStyles();
  const history = useHistory();

  const [openValue, setOpenValue] = useState(false);
  const [openCrm, setOpenCrm] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [clickTimer, setClickTimer] = useState(null);

  const lastMessageTimeClass =
    Number(ticket.unreadMessages) > 0
      ? classes.lastMessageTimeUnread
      : classes.lastMessageTime;

  // Valor único: ticket.valorVenda
  const opportunityValue = ticket.valorVenda != null ? Number(ticket.valorVenda) : null;

  const isExpired = ticket.dataFim &&
    isPast(new Date(ticket.dataFim + "T23:59:59")) &&
    !["Sucesso", "Perdido"].includes(ticket.crmStatus);

  const formatDate = (d) => {
    if (!d) return null;
    try { return format(parseISO(d.substring(0, 10) + "T12:00:00"), "dd/MM/yyyy"); }
    catch { return d; }
  };

  const formatLastInteraction = (updatedAt) => {
    if (!updatedAt) return null;
    return isSameDay(parseISO(updatedAt), new Date())
      ? format(parseISO(updatedAt), 'HH:mm')
      : format(parseISO(updatedAt), 'dd/MM/yyyy HH:mm');
  };

  const handleClick = (e) => {
    if (openCrm) return;
    if (clickTimer) {
      clearTimeout(clickTimer);
      setClickTimer(null);
      setOpenCrm(true);
    } else {
      const timer = setTimeout(() => {
        setClickTimer(null);
        if (!openCrm) history.push(`/tickets/${ticket.uuid}`);
      }, 250);
      setClickTimer(timer);
    }
  };

  const handleOpenValue = (e) => {
    e.stopPropagation();
    setNewValue(opportunityValue != null ? String(opportunityValue) : '');
    setOpenValue(true);
  };

  const handleSaveValue = async () => {
    const parsed = newValue !== '' ? parseFloat(newValue) : null;
    await api.put(`/tickets/${ticket.id}`, { valorVenda: parsed });
    updateTicket({ ...ticket, valorVenda: parsed });
    setOpenValue(false);
  };

  const handleRemoveValue = async (e) => {
    e.stopPropagation();
    await api.put(`/tickets/${ticket.id}`, { valorVenda: null });
    updateTicket({ ...ticket, valorVenda: null });
  };

  const empresa = ticket.contact?.razaoSocial || ticket.contact?.segmento || null;

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.card}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.85 : 1,
            borderLeft: isExpired ? '3px solid #f44336' : undefined,
          }}
          onClick={handleClick}
        >
          {/* ── Cabeçalho ── */}
          <div className={classes.header}>
            <div className={classes.leftHeader}>
              <Avatar src={ticket.contact?.urlPicture} className={classes.avatar} />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={ticket.contact?.name || ''}>
                    <Typography className={isExpired ? classes.cardTitleExpired : classes.cardTitle}>
                      {ticket.contact?.name || '—'}
                    </Typography>
                  </Tooltip>
                  {ticket.crmStatus && (
                    <span
                      className={classes.crmStatus}
                      style={CRM_STATUS_COLORS[ticket.crmStatus] || CRM_STATUS_COLORS["Não iniciado"]}
                    >
                      {ticket.crmStatus}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Typography className={classes.ticketNumber}>#{ticket.id}</Typography>
          </div>

          <Divider className={classes.divider} />

          {isExpired && (
            <Typography className={classes.expiredBadge}>⚠ Prazo vencido</Typography>
          )}

          {empresa && (
            <div className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Empresa</Typography>
              <Tooltip title={empresa}>
                <Typography className={classes.infoValue}>{empresa}</Typography>
              </Tooltip>
            </div>
          )}

          {ticket.user && (
            <div className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Atendente</Typography>
              <Typography className={classes.infoValue}>{ticket.user.name}</Typography>
            </div>
          )}

          <div className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Valor</Typography>
            {opportunityValue != null ? (
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Typography className={classes.infoValueGreen} onClick={handleOpenValue} style={{ cursor: 'pointer' }}>
                  R$ {opportunityValue.toFixed(2)}
                </Typography>
                <Tooltip title="Remover valor">
                  <IconButton style={{ padding: 0, marginLeft: 4, color: '#e53935' }} onClick={handleRemoveValue} size="small">
                    <CloseIcon style={{ fontSize: '0.75rem' }} />
                  </IconButton>
                </Tooltip>
              </div>
            ) : (
              <Typography
                className={classes.infoValue}
                onClick={handleOpenValue}
                style={{ cursor: 'pointer', color: '#bbb', fontStyle: 'italic' }}
              >
                Atribuir valor
              </Typography>
            )}
          </div>

          {ticket.dataFim && (
            <div className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Data limite</Typography>
              <Typography className={isExpired ? classes.infoValueExpired : classes.infoValue}>
                {formatDate(ticket.dataFim)}{isExpired && ' ⚠'}
              </Typography>
            </div>
          )}

          {ticket.updatedAt && (
            <div className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Última interação</Typography>
              <Typography className={lastMessageTimeClass} style={{ fontSize: '0.75rem', flex: 1 }}>
                {formatLastInteraction(ticket.updatedAt)}
              </Typography>
            </div>
          )}

          <div className={classes.footer}>
            <Button
              size="small"
              className={classes.cardButton}
              onClick={(e) => { e.stopPropagation(); history.push(`/tickets/${ticket.uuid}`); }}
            >
              Ver Ticket
            </Button>
          </div>

          {/* Modal de edição rápida de valor */}
          <Dialog open={openValue} onClose={() => setOpenValue(false)} classes={{ paper: classes.dialogPaper }} onClick={e => e.stopPropagation()}>
            <DialogTitle>{opportunityValue != null ? 'Editar' : 'Atribuir'} Valor</DialogTitle>
            <DialogContent>
              <TextField
                label="Valor (R$)" type="number" fullWidth value={newValue}
                onChange={e => setNewValue(e.target.value)}
                variant="outlined" size="small" className={classes.textField}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenValue(false)} color="secondary" variant="outlined" className={classes.dialogButton}>Cancelar</Button>
              <Button onClick={handleSaveValue} color="primary" variant="outlined" className={classes.dialogButton}>Salvar</Button>
            </DialogActions>
          </Dialog>

          {/* Modal CRM (double-click) */}
          <div onClick={e => e.stopPropagation()}>
            <CrmCardModal
              open={openCrm}
              onClose={() => setOpenCrm(false)}
              ticket={ticket}
              onSaved={(updatedTicket) => updateTicket(updatedTicket)}
              lanes={laneOptions}
            />
          </div>
        </div>
      )}
    </Draggable>
  );
});

export default KanbanCard;
